import React, { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import PanelLayoutContent from "components/layout/PanelLayoutContent"
import { useNavigate, useParams } from "react-router-dom"
import { TslNewsletterService } from "services/api-workers-service"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button, { IconButton } from "ui/components/atoms/button"
import Spinner from "ui/components/atoms/spinner"
import Tag from "ui/components/atoms/tag"
import { H2, Label, P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import { formatDate } from "utils/date"

import { localeToFlag, positionLabel, tagLabel } from "./data"
import TslNewsletterNav from "./TslNewsletterNav"
import SubscriberDeleteModal from "./SubscriberDeleteModal"

const SubscriberDetail = () => {
  const { subscriberId = "" } = useParams()
  const navigate = useNavigate()
  const [showDelete, setShowDelete] = useState(false)

  const { data: subscriber, isLoading } = useQuery({
    queryKey: ["tsl", "subscriber", subscriberId],
    queryFn: () => TslNewsletterService.getSubscriber(subscriberId),
    enabled: !!subscriberId,
  })

  if (isLoading) {
    return (
      <PanelLayoutContent title="TSL Newsletter" text="Szczegóły subskrybenta">
        <TslNewsletterNav />
        <Row $justifyContent="center" $padding="M">
          <Spinner $size="30px" />
        </Row>
      </PanelLayoutContent>
    )
  }

  if (!subscriber) {
    return (
      <PanelLayoutContent title="TSL Newsletter" text="Szczegóły subskrybenta">
        <TslNewsletterNav />
        <Alert $type="error">Nie znaleziono subskrybenta.</Alert>
      </PanelLayoutContent>
    )
  }

  return (
    <PanelLayoutContent title="TSL Newsletter" text="Szczegóły subskrybenta">
      <TslNewsletterNav />

      <Row $alignItems="center" $marginBottom="M">
        <IconButton
          $background="background"
          $size="45px"
          $iconSize="L"
          $borderRadius="borderRadius"
          onClick={() => navigate("/panel/tsl-newsletter/subscribers")}
          icon="arrow-left"
          $marginRight="S"
          ariaLabel="Wstecz"
        />
        <H2 $marginBottom="0">{subscriber.email}</H2>
        <Tag
          $type="secondary"
          $showBorder={false}
          $background={subscriber.active ? "greenBackground" : "background"}
          $color={subscriber.active ? "green" : "third"}
          label={subscriber.active ? "Aktywny" : "Nieaktywny"}
          $marginLeft="S"
        />
      </Row>

      <Card>
        <Row style={{ flexWrap: "wrap" }} $marginBottom="S">
          <Field>
            <Label $type="secondary" $marginBottom="XS">
              Imię i nazwisko
            </Label>
            <P $marginBottom="0">
              {[subscriber.first_name, subscriber.last_name].filter(Boolean).join(" ") || "—"}
            </P>
          </Field>
          <Field>
            <Label $type="secondary" $marginBottom="XS">
              Stanowisko
            </Label>
            <P $marginBottom="0">{positionLabel(subscriber.position)}</P>
          </Field>
          <Field>
            <Label $type="secondary" $marginBottom="XS">
              Język
            </Label>
            <P $marginBottom="0">
              {localeToFlag(subscriber.language)} {subscriber.language}
            </P>
          </Field>
        </Row>

        <Row style={{ flexWrap: "wrap" }} $marginBottom="S">
          <Field>
            <Label $type="secondary" $marginBottom="XS">
              Data zapisu
            </Label>
            <P $marginBottom="0">{formatDate(subscriber.created_at)}</P>
          </Field>
          <Field>
            <Label $type="secondary" $marginBottom="XS">
              Ostatnia aktualizacja
            </Label>
            <P $marginBottom="0">{formatDate(subscriber.updated_at)}</P>
          </Field>
        </Row>

        <Box $marginBottom="M">
          <Label $type="secondary" $marginBottom="XS">
            Tagi
          </Label>
          {subscriber.tags?.length > 0 ? (
            <TagsRow>
              {subscriber.tags.map(t => (
                <Tag
                  key={t}
                  $type="secondary"
                  $showBorder={false}
                  $background="background"
                  label={tagLabel(t)}
                />
              ))}
            </TagsRow>
          ) : (
            <P $marginBottom="0">—</P>
          )}
        </Box>

        <Row $justifyContent="flex-end">
          <Button $type="secondary" icon="trash" onClick={() => setShowDelete(true)}>
            Usuń (RODO)
          </Button>
        </Row>
      </Card>

      <SubscriberDeleteModal
        subscriber={showDelete ? subscriber : null}
        setSubscriber={s => {
          if (!s) setShowDelete(false)
        }}
        onDeleted={() => navigate("/panel/tsl-newsletter/subscribers")}
      />
    </PanelLayoutContent>
  )
}

export default SubscriberDetail

const Card = styled(Box)`
  background: white;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  padding: ${({ theme }) => theme.space.M};
`

const Field = styled(Box)`
  min-width: 200px;
  margin-right: ${({ theme }) => theme.space.M};
  margin-bottom: ${({ theme }) => theme.space.S};
`

const TagsRow = styled(Row)`
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.XXS};
`
